import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, Checkbox, IconButton, Button } from '@mui/material';
import { useTheme } from '@mui/system';
import { Delete, Edit, Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import ApiHelper from 'utils/apiHelper';
import AddPermissionGroupDialog from 'containers/Management/Permissions/AddPermissionGroupDialog';
import EditPermissionGroupDialog from 'containers/Management/Permissions/EditPermissionGroupDialog';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';

const ManagementPermissions = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [usersData, setUsersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissionGroupsData, setPermissionGroupsData] = useState([]);
    const [showAddPermissionGroup, setShowAddPermissionGroup] = useState(false);
    const [showEditPermissionGroup, setShowEditPermissionGroup] = useState(false);
    const [selectedPermissionGroup, setSelectedPermissionGroup] = useState('');
    const [deletePermissionGroupId, setDeletePermissionGroupId] = useState();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    const { userInfo } = useSelector((state) => state.auth);
    const manageOrganizationData = JSON.parse(localStorage.getItem('manageOrganization'));

    useEffect(() => {
        getPermissionGroups();
        getUsersData();
    }, []);

    const getPermissionGroups = () => {
        setLoading(true);
        ApiHelper.get('/api/management/permission_group/')
            .then((res) => {
                setPermissionGroupsData(res.data);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get permission groups data');
                    setLoading(false);
                }
            });
    };

    const getUsersData = () => {
        ApiHelper.get('/api/management/users/')
            .then((res) => {
                setUsersData(res.data.users);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the users data');
                }
            });
    };

    const handleAddPermissionGroup = () => {
        if (usersData.length === 0) {
            toast.warning('There is no users data, please create a new user');
            navigate('/management/users');
        } else {
            setShowAddPermissionGroup(true);
        }
    };

    const handleShowAddPermissionGroup = () => {
        setShowAddPermissionGroup(false);
        getPermissionGroups();
    };

    const handleCloseEditPermissionGroup = () => {
        setShowEditPermissionGroup(false);
        getPermissionGroups();
    };

    const handleDeleteConfirmation = (data) => {
        if (data.name === 'Organization Administration') {
            toast.warning("This organization can't be deleted.");
            return true;
        }
        setShowDeleteConfirmationDialog(true);
        setDeletePermissionGroupId(data.id);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleDeletePermissionGroup = () => {
        ApiHelper.delete(`/api/management/permission_group/${deletePermissionGroupId}/`)
            .then(() => {
                toast.success('Permission Group was deleted successfully.');
                getPermissionGroups();
                handleClose();
            })
            .catch(() => {
                toast.error('Failed to delete the permission group.');
                handleClose();
            });
    };

    const handleEditPermissionGroup = (data) => {
        setShowEditPermissionGroup(true);
        setSelectedPermissionGroup(data);
    };

    if (!permissionGroupsData) {
        return null;
    } else {
        return (
            <MainCard title="Permission Management" cardHeaderShow={false}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>
                                    {manageOrganizationData?.orgid ? manageOrganizationData.name : userInfo.user.orgid.name}
                                </Typography>
                            </Grid>
                            <Grid item sm={3} md={3}>
                                <Button
                                    sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={handleAddPermissionGroup}
                                    startIcon={<Add />}
                                >
                                    New Permission Group
                                </Button>
                                {showAddPermissionGroup && (
                                    <AddPermissionGroupDialog usersData={usersData} onClose={handleShowAddPermissionGroup} />
                                )}
                            </Grid>
                        </Grid>
                        <Box sx={{ width: '100%', height: '100%', typography: 'body1' }}>
                            {loading === true ? (
                                <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                    Please wait...
                                </Typography>
                            ) : permissionGroupsData.length === 0 ? (
                                <Typography sx={{ mt: 3, textAlign: 'center' }}>No Permission Groups Data</Typography>
                            ) : (
                                <Table sx={{ minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Permission Group</TableCell>
                                            <TableCell>Root Admin</TableCell>
                                            <TableCell>Server Admin</TableCell>
                                            <TableCell>Org Admin</TableCell>
                                            <TableCell>User Admin</TableCell>
                                            <TableCell>Group Admin</TableCell>
                                            <TableCell>Options</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {permissionGroupsData.map((data, key) => (
                                            <TableRow key={key}>
                                                <TableCell>
                                                    <Typography>{data.name}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Checkbox checked={data.root_admin} disabled={true} />
                                                </TableCell>
                                                <TableCell>
                                                    <Checkbox checked={data.server_admin} disabled={true} />
                                                </TableCell>
                                                <TableCell>
                                                    <Checkbox checked={data.org_admin} disabled={true} />
                                                </TableCell>
                                                <TableCell>
                                                    <Checkbox checked={data.user_admin} disabled={true} />
                                                </TableCell>
                                                <TableCell>
                                                    <Checkbox checked={data.group_admin} disabled={true} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <IconButton size="small" onClick={() => handleEditPermissionGroup(data)}>
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => handleDeleteConfirmation(data)}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {showEditPermissionGroup && (
                                            <EditPermissionGroupDialog
                                                data={selectedPermissionGroup}
                                                usersData={usersData}
                                                onClose={handleCloseEditPermissionGroup}
                                                onCloseCancel={() => setShowEditPermissionGroup(false)}
                                            />
                                        )}
                                        {showDeleteConfirmationDialog && (
                                            <DeleteConfirmationDialog
                                                onClose={handleClose}
                                                onYes={handleDeletePermissionGroup}
                                                name="permission group"
                                            />
                                        )}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementPermissions;
